import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import contractService from "../../services/contract";
import userService from "../../services/users";
import queryDefault from "../../utils/queryDefault";

interface ContractFormValues {
  name: string;
  pob: string;
  dob: string;
  last_education: string;
  gender: string;
  religion: string;
  address: string;
  id_number: string;
  email: string;
  phone: string;
  branch: string;
  branch_address: string;
  placement_location: string;
  salary: string;
  salary_type: string;
  employee_id: string;
  officer_id: string;
  contract_start: string;
  contract_end: string;
  project_id: string;
  username: string;
  status: string;
  allowance_health: string;
  allowance_operational: string;
  id: string | undefined;
}

export default function ContractEdit() {
  const notification = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const [isViewOnly, setIsViewOnly] = useState<boolean>(false);
  const [contractData, setContractData] = useState<any>(null);

  const schema = yup.object().shape({
    name: yup.string().required("Name is required"),
    pob: yup.string().required("Place of birth is required"),
    dob: yup.string().required("Date of birth is required"),
    last_education: yup.string().required("Last education is required"),
    gender: yup.string().required("Gender is required"),
    religion: yup.string().required("Religion is required"),
    address: yup.string().required("Address is required"),
    id_number: yup.string().required("ID number is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup.string().required("Phone is required"),
    branch: yup.string().required("Branch is required"),
    branch_address: yup.string().required("Branch address is required"),
    placement_location: yup.string().required("Placement location is required"),
    salary: yup.string().required("Salary is required"),
    salary_type: yup.string().required("Salary type is required"),
    contract_start: yup.string().required("Contract start date is required"),
    contract_end: yup.string().required("Contract end date is required"),
  });

  const form = useForm<ContractFormValues>({
    defaultValues: {
      name: "",
      pob: "",
      dob: "",
      last_education: "",
      gender: "",
      religion: "",
      address: "",
      id_number: "",
      email: "",
      phone: "",
      branch: "",
      branch_address: "",
      placement_location: "",
      salary: "",
      salary_type: "monthly",
      employee_id: "",
      officer_id: user.id || "",
      contract_start: "",
      contract_end: "",
      allowance_health: "",
      allowance_operational: "",
    },
    resolver: yupResolver(schema),
  });

  const employeeRespondToContract = useMutation(
    (params: { id: number; action: string; rejectionReason?: string }) => {
      if (params.action === "approve") {
        return contractService.employeeApproveContract({
          id: params.id,
          employee_id: user.id,
          last_updated_by: user.name || user.username
        });
      } else {
        return contractService.employeeRejectContract({
          id: params.id,
          employee_id: user.id,
          rejection_reason: params.rejectionReason || "Contract details not acceptable",
          last_updated_by: user.name || user.username
        });
      }
    },
    {
      onSuccess: () => {
        navigate("/app/contract");
        notification.onOpen({
          message: "Contract response submitted successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to respond to contract",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const hrRespondToContract = useMutation(
    (params: { id: number; action: string; rejectionReason?: string }) => {
      if (params.action === "approve") {
        return contractService.hrApproveContract({
          id: params.id,
          hr_id: user.id,
          last_updated_by: user.name || user.username,
          role_id: user.role_id
        });
      } else {
        return contractService.hrRejectContract({
          id: params.id,
          hr_id: user.id,
          rejection_reason: params.rejectionReason || "Document not properly signed",
          last_updated_by: user.name || user.username,
          role_id: user.role_id
        });
      }
    },
    {
      onSuccess: () => {
        navigate("/app/contract");
        notification.onOpen({
          message: "Contract response submitted successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to respond to contract",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const updateDraftContract = useMutation(
    (values: ContractFormValues) => {
      return contractService.updateDraftContract({
        ...values,
        role_id: user.role_id,
        last_updated_by: user.name || user.username
      });
    },
    {
      onSuccess: () => {
        const employeeId = form.getValues().employee_id;
        if (employeeId) {
          navigate(`/app/dashboard/employee/${employeeId}`);
        } else {
          navigate("/app/contract");
        }
        notification.onOpen({
          message: "Contract draft was successfully updated!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to update contract draft",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const uploadSignedContract = useMutation(
    (file: File) => {
      return contractService.uploadSignedContract(Number(id), file);
    },
    {
      onSuccess: () => {
        notification.onOpen({
          message: "Signed contract uploaded successfully!",
          type: "success",
          position: "top",
        });
        contractService.viewContractDetail(id).then(({ data }) => {
          setContractData(data);
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to upload signed contract",
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );

  const handleContractResponse = (action: 'approve' | 'reject', rejectionReason?: string) => {
    const contractStatus = form.watch("status");
    const params = { id: Number(id), action, rejectionReason };
    if ((contractStatus === 'employee_review' || contractStatus === 'pending_upload')
      && user.id === form.getValues().employee_id) {
      employeeRespondToContract.mutate(params);
    } else if (contractStatus === 'hr_review' && user.role_id === 1) {
      hrRespondToContract.mutate(params);
    }
  };

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadSignedContract.mutate(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const updateContract = useMutation(contractService.updateContract, {
    onSuccess: () => {
      const employeeId = form.getValues().employee_id;
      if (employeeId) {
        navigate(`/app/employee/${employeeId}`);
      } else {
        navigate("/app/contract");
      }
      notification.onOpen({
        message: "Contract was successfully updated!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Something went wrong",
        type: "error",
        position: "top",
      });
    },
    ...queryDefault,
  });

  const onSubmit = (values: ContractFormValues) => {
    const contractStatus = form.watch("status");
    if (contractStatus === 'draft' && user.role_id === 1) {
      updateDraftContract.mutate({
        ...values,
        id,
      });
    } else {
      updateContract.mutate({
        ...values,
        id,
      });
    }
  };

  useEffect(() => {
    if (id) {
      contractService.viewContractDetail(id).then((response) => {
        const data = response.contract;
        setContractData(data);
        const shouldBeViewOnly = data.status === 'hr_review' && user.id === data.employee_id;
        setIsViewOnly(shouldBeViewOnly);
        form.setValue("name", data.name || "");
        form.setValue("pob", data.pob || "");
        form.setValue("dob", data.dob ? dayjs(data.dob).format("YYYY-MM-DD") : "");
        form.setValue("last_education", data.last_education || "");
        form.setValue("gender", data.gender || "");
        form.setValue("religion", data.religion || "");
        form.setValue("address", data.address || "");
        form.setValue("id_number", data.id_number || "");
        form.setValue("email", data.email || "");
        form.setValue("phone", data.phone || "");
        form.setValue("branch", data.branch || "");
        form.setValue("branch_address", data.branch_address || "");
        form.setValue("placement_location", data.placement_location || "");
        form.setValue("salary", data.salary || "");
        form.setValue("salary_type", data.salary_type || "monthly");
        form.setValue("employee_id", data.employee_id || "");
        form.setValue("officer_id", data.officer_id || user.id || "");
        form.setValue("contract_start", data.contract_start ? dayjs(data.contract_start).format("YYYY-MM-DD") : "");
        form.setValue("contract_end", data.contract_end ? dayjs(data.contract_end).format("YYYY-MM-DD") : "");
        form.setValue("status", data.status || "");
        form.setValue("allowance_health", data.allowance_health || "");
        form.setValue("allowance_operational", data.allowance_operational || "");
      });
    }
  }, [id, form, user.id]);

  const { data: employeeData } = useQuery(
    ["employee", form.watch("employee_id")],
    () => userService.viewUser(form.watch("employee_id")),
    {
      ...queryDefault,
      enabled: !!form.watch("employee_id"),
    }
  );

  const canRespondToContract = () => {
    const contractStatus = form.watch("status");
    const employeeId = form.watch("employee_id");
    if (contractStatus === 'employee_review') {
      return user.id === employeeId;
    }
    if (contractStatus === 'hr_review') {
      return user.role_id === 2;
    }
    return false;
  };

  return (
    <MainLayout>
      <Breadcrumb
        label="Contract Details"
        breadcrumbs={
          form.watch("employee_id")
            ? [
              { label: "Dashboard", href: "/app/dashboard" },
              { label: "Employee Details", href: `/app/employee/${form.watch("employee_id")}` }
            ]
            : [{ label: "Contract Management", href: "/app/contract" }]
        }
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        {form.watch("employee_id") && employeeData?.data && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary">
              Contract for: {employeeData.data.name}
            </Typography>
          </Box>
        )}

        {isViewOnly && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>View Only Mode</AlertTitle>
            This contract is currently under HR review. You can view the details but cannot make changes at this time.
          </Alert>
        )}

        {contractData?.status === 'employee_review' && user.id === contractData?.employee_id && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff9e6' }}>
            <Typography variant="h6" gutterBottom>
              Contract Review Required
            </Typography>
            <Typography variant="body1" paragraph>
              Please review this contract carefully. Once approved, it will be sent to HR for final review.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                color="success"
                onClick={() => handleContractResponse('approve')}
              >
                Approve Contract
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleContractResponse('reject')}
              >
                Reject Contract
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Add upload section for pending_upload status */}
        {contractData?.status === 'pending_upload' && user.id === contractData?.employee_id && (
          <Paper sx={{ p: 2, mb: 3, bgcolor: '#e6f7ff' }}>
            <Typography variant="h6" gutterBottom>
              Upload Signed Contract
            </Typography>
            <Typography variant="body1" paragraph>
              Please upload the signed version of your contract document. The document should be in PDF format.
            </Typography>
            <Stack direction="row" spacing={2}>
              <input
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
                data-testid="file-upload-input"
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleFileUpload}
                disabled={uploadSignedContract.isLoading}
              >
                {uploadSignedContract.isLoading ? 'Uploading...' : 'Upload Signed Contract'}
              </Button>
            </Stack>
          </Paper>
        )}

        <Box sx={{ mt: 3 }}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Personal Information
                </Typography>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Full Name</FormLabel>
                  <Controller
                    name="name"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Place of Birth</FormLabel>
                  <Controller
                    name="pob"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Date of Birth</FormLabel>
                  <Controller
                    name="dob"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Last Education</FormLabel>
                  <Controller
                    name="last_education"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                        disabled={isViewOnly}
                      >
                        <MenuItem value="SD">SD</MenuItem>
                        <MenuItem value="SMP">SMP</MenuItem>
                        <MenuItem value="SMA">SMA</MenuItem>
                        <MenuItem value="D3">D3</MenuItem>
                        <MenuItem value="S1">S1</MenuItem>
                        <MenuItem value="S2">S2</MenuItem>
                        <MenuItem value="S3">S3</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.last_education && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.last_education.message}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Gender</FormLabel>
                  <Controller
                    name="gender"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                        disabled={isViewOnly}
                      >
                        <MenuItem value="Laki-Laki">Laki-Laki</MenuItem>
                        <MenuItem value="Perempuan">Perempuan</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.gender && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.gender.message}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Religion</FormLabel>
                  <Controller
                    name="religion"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                        disabled={isViewOnly}
                      >
                        <MenuItem value="Islam">Islam</MenuItem>
                        <MenuItem value="Kristen">Kristen</MenuItem>
                        <MenuItem value="Katolik">Katolik</MenuItem>
                        <MenuItem value="Hindu">Hindu</MenuItem>
                        <MenuItem value="Budha">Budha</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.religion && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.religion.message}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>ID Number (KTP)</FormLabel>
                  <Controller
                    name="id_number"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Email</FormLabel>
                  <Controller
                    name="email"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="email"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Phone</FormLabel>
                  <Controller
                    name="phone"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Address</FormLabel>
                  <Controller
                    name="address"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        multiline
                        rows={4}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Employment Information */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Employment Information
                </Typography>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Branch</FormLabel>
                  <Controller
                    name="branch"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Branch Address</FormLabel>
                  <Controller
                    name="branch_address"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        multiline
                        rows={2}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Placement Location</FormLabel>
                  <Controller
                    name="placement_location"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Salary</FormLabel>
                  <Controller
                    name="salary"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        type="number"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Salary Type</FormLabel>
                  <Controller
                    name="salary_type"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <Select
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        size="small"
                        disabled={isViewOnly}
                      >
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="daily">Daily</MenuItem>
                      </Select>
                    )}
                  />
                  {form.formState.errors.salary_type && (
                    <Typography color="error" variant="caption">
                      {form.formState.errors.salary_type.message}
                    </Typography>
                  )}
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Allowance Health</FormLabel>
                  <Controller
                    name="allowance_health"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        type="number"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Allowance Operational</FormLabel>
                  <Controller
                    name="allowance_operational"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        type="number"
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>

                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel>Status</FormLabel>
                  <Controller
                    name="status"
                    control={form.control}
                    render={({ field: { ref, ...field } }) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        size="small"
                        disabled
                        InputProps={{
                          readOnly: true,
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              {/* Contract Period */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Contract Period
                </Typography>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Contract Start Date</FormLabel>
                  <Controller
                    name="contract_start"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item lg={6} xs={12}>
                <FormControl fullWidth sx={{ mt: 2 }}>
                  <FormLabel required>Contract End Date</FormLabel>
                  <Controller
                    name="contract_end"
                    control={form.control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <TextField
                        {...field}
                        type="date"
                        inputRef={ref}
                        error={Boolean(fieldState.error?.message)}
                        helperText={fieldState.error?.message}
                        size="small"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        disabled={isViewOnly}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => form.watch("employee_id")
                  ? navigate(`/app/dashboard/employee/${form.watch("employee_id")}`)
                  : navigate("/app/contract")
                }
              >
                {isViewOnly ? "Back" : "Cancel"}
              </Button>

              {!isViewOnly && !canRespondToContract() && (
                <LoadingButton
                  loading={updateContract.isLoading}
                  type="submit"
                  variant="contained"
                >
                  Update
                </LoadingButton>
              )}

              {canRespondToContract() && contractData?.status === 'hr_review' && user.role_id === 2 && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleContractResponse('approve')}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleContractResponse('reject')}
                  >
                    Reject
                  </Button>
                </>
              )}
            </Stack>
          </form>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}